/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AccountsApiService } from '../../modules/centralisedManagement/shared/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../core/models';
import { ExternalUtilsService } from '../shared/external-utils-service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoadingService, ModalService, NotifyService, ValidationService } from '../../core/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { version } from '../../version';

@Component({
    selector: 'app-login',
    templateUrl: './registration.component.html',
    styleUrls: ['registration.component.scss', '../externalPageStyles.scss'],
})

export class RegistrationComponent {
    /**
     * Show register success message.
     */
    public showSuccess: boolean = false;

    /**
     * Registration form.
     */
    public registerForm: FormGroup;

    /**
     * The client version
     */
    public clientVersion: string;

    /**
     * As we are not extending base we need reference to destroy.
     * @protected
     */
    protected destroyed$: Subject<boolean> = new Subject();

    constructor(
        private accountsApiService: AccountsApiService,
        private externalUtilsService: ExternalUtilsService,
        private formBuilder: FormBuilder,
        private loadingService: LoadingService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private route: ActivatedRoute,
        private router: Router
    ) {
        this.clientVersion = version;

        this.setupFormGroups();
        this.setEmailParameterInForm();
    }

    /**
     * Set email from parameter in form.
     */
    public setEmailParameterInForm(): void {
        this.route.queryParams.subscribe({
            next: (params: any): void => {
                if (params?.email) {
                    this.registerForm.patchValue({ emailAddress: params.email });
                }
            }
        });
    }

    /**
     * Create account.
     */
    public createAccount(): void {
        const userObject: any = { email: this.registerForm.get('emailAddress').value };
        this.accountsApiService.create(userObject)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.showSuccess = true;
                },
                error: (error: ErrorResponseModel): void => {
                    const message: string = this.externalUtilsService.getMessageFromError(error);

                    this.loadingService.loadingButtonEmitter.next(false);
                    this.notifyService.error(message, { id: 'createAccount' });
                }
            });
    }

    /**
     * Open policies modal.
     * @param modalId
     */
    public openTermsModal(modalId: string): void {
        this.modalService.openModalInParent(modalId);
    }

    /**
     * Redirect user to login page
     */
    public signIn(): void {
        this.router.navigateByUrl('/login').then().catch();
    }

    /**
     * Setup page form groups.
     * @private
     */
    private setupFormGroups(): void {
        // Set up our forms
        this.registerForm = this.formBuilder.group({
            emailAddress: [null, [Validators.required, ValidationService.emailValidator]]
        });
    }
}
